/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Modal } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import moment from 'moment';

import {
  getProductTariffHistory,
  IProductTariffHistoryEntry,
  ITariffDiff,
  ITariffProgram,
  ITariffFieldChange,
} from 'api/bitedb/tariffs/tariffs.service';

interface TariffHistoryModalProps {
  open: boolean;
  handleClose: () => void;
  databaseProductId?: string;
  productName?: string;
}

const fmtMoney = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(Number(v))
    ? '—'
    : `$${Number(v).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const programLabel = (p: ITariffProgram): string => {
  const code = p.code || p._codeDigits || '';
  const type = p.type ? `${p.type} ` : '';
  const rate = p.rate ? ` @ ${p.rate}` : '';
  return `${type}${code}${rate}`.trim();
};

// A small pill; color is driven by the status CSS tokens so both themes work.
const Pill: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      margin: '2px 4px 2px 0',
      borderRadius: '6px',
      fontFamily: 'Inter, sans-serif',
      fontSize: '12px',
      lineHeight: '18px',
      color: color,
      background: `color-mix(in srgb, ${color} 12%, transparent)`,
      border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
    }}
  >
    {children}
  </span>
);

// One "X → Y" row, only rendered when the field actually changed.
const DeltaRow: React.FC<{
  label: string;
  change: ITariffFieldChange<string | number> | undefined;
  format?: (v: any) => string;
}> = ({ label, change, format }) => {
  if (!change || !change.changed) {
    return null;
  }
  const fmt = format || ((v: any) => (v === null || v === undefined ? '—' : String(v)));
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        padding: '3px 0',
        fontSize: '13px',
      }}
    >
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontFamily: 'Inter, sans-serif' }}>
        <span style={{ color: 'var(--text-tertiary)' }}>{fmt(change.from)}</span>
        <span style={{ color: 'var(--text-tertiary)', margin: '0 6px' }}>→</span>
        <span style={{ color: 'var(--text-primary-color)', fontWeight: 500 }}>{fmt(change.to)}</span>
      </span>
    </div>
  );
};

const TariffHistoryModal: React.FC<TariffHistoryModalProps> = ({
  open,
  handleClose,
  databaseProductId,
  productName,
}) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<IProductTariffHistoryEntry[]>([]);

  useEffect(() => {
    let active = true;
    if (open && databaseProductId) {
      setLoading(true);
      getProductTariffHistory(databaseProductId)
        .then((rows) => {
          if (active) {
            setHistory(rows);
          }
        })
        .finally(() => {
          if (active) {
            setLoading(false);
          }
        });
    } else if (!open) {
      setHistory([]);
    }
    return () => {
      active = false;
    };
  }, [open, databaseProductId]);

  const renderEntry = (entry: IProductTariffHistoryEntry, index: number) => {
    const when = entry.screen_date || entry.created_at;
    const dateLabel = when ? moment(when).format('MM/DD/YYYY') : '—';
    const diff = entry.tariff_diff as ITariffDiff | null;
    const isBase = entry.is_base_snapshot || !diff;

    return (
      <div
        key={entry.id || index}
        className='results-card'
        style={{
          padding: '12px 14px',
          marginBottom: '10px',
          boxShadow: 'var(--es-card-shadow)',
          border: '1px solid var(--es-soft-border)',
          borderRadius: '10px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
          }}
        >
          <span
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              color: 'var(--text-primary-color)',
            }}
          >
            {dateLabel}
          </span>
          <Pill color={isBase ? 'var(--count-color)' : 'var(--full-bar-color)'}>
            {isBase ? 'Initial tariff' : 'Tariff changed'}
          </Pill>
        </div>

        {isBase ? (
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span>Total cost</span>
              <span style={{ color: 'var(--text-primary-color)', fontFamily: 'Inter, sans-serif' }}>
                {fmtMoney(entry.summary?.total_cost)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
              <span>Base duty</span>
              <span style={{ color: 'var(--text-primary-color)', fontFamily: 'Inter, sans-serif' }}>
                {entry.summary?.base_duty_rate || '—'}
              </span>
            </div>
            {entry.summary?.status_message ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                <span>Status</span>
                <span style={{ color: 'var(--text-primary-color)' }}>
                  {entry.summary.status_message}
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div>
            <DeltaRow label='Total cost' change={diff?.total_cost} format={fmtMoney} />
            <DeltaRow label='Duty' change={diff?.duty_cost} format={fmtMoney} />
            <DeltaRow label='Additional duty' change={diff?.additional_duty} format={fmtMoney} />
            <DeltaRow label='Base duty rate' change={diff?.base_duty_rate} />
            <DeltaRow label='Status' change={diff?.status_message} />

            {(diff?.programs_added ?? []).length > 0 ? (
              <div style={{ marginTop: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Added</span>
                <div style={{ marginTop: '4px' }}>
                  {(diff?.programs_added ?? []).map((p, i) => (
                    <Pill key={`a-${i}`} color='var(--full-bar-color)'>
                      {programLabel(p)}
                    </Pill>
                  ))}
                </div>
              </div>
            ) : null}

            {(diff?.programs_removed ?? []).length > 0 ? (
              <div style={{ marginTop: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Removed</span>
                <div style={{ marginTop: '4px' }}>
                  {(diff?.programs_removed ?? []).map((p, i) => (
                    <Pill key={`r-${i}`} color='var(--low-color)'>
                      {programLabel(p)}
                    </Pill>
                  ))}
                </div>
              </div>
            ) : null}

            {(diff?.programs_changed ?? []).length > 0 ? (
              <div style={{ marginTop: '8px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Rate changes</span>
                <div style={{ marginTop: '4px' }}>
                  {(diff?.programs_changed ?? []).map((p, i) => (
                    <Pill key={`c-${i}`} color='var(--mid-color)'>
                      {`${p.type ? `${p.type} ` : ''}${p.code}: ${p.from_rate} → ${p.to_rate}`}
                    </Pill>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div
        className='audit-add-modal-cont'
        style={{ width: '560px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className='audit-add-modal-cont-head'>
          <div className='audit-add-modal-head-cont-change'>
            <div className='audit-add-modal-head-cont-icon'>
              <HistoryOutlinedIcon sx={{ fontSize: 22, color: 'var(--action-color)' }} />
            </div>
            <div className='audit-add-modal-head-cont-supporting'>
              <h1>Tariff history</h1>
              <p>
                {productName
                  ? `How tariffs have changed over time for ${productName}`
                  : 'How tariffs have changed over time'}
              </p>
            </div>
          </div>
          <div className='audit-add-modal-head-close' onClick={handleClose}>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M18 6L6 18M6 6L18 18'
                stroke='#85888E'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        </div>
        <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>
        <div
          className='audit-add-modal-cont-body'
          style={{ paddingTop: '12px', overflowY: 'auto', flex: 1 }}
        >
          {loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '160px',
              }}
            >
              <CircularProgress color='primary' />
            </div>
          ) : history.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '160px',
                color: 'var(--text-secondary)',
                textAlign: 'center',
              }}
            >
              <HistoryOutlinedIcon sx={{ fontSize: 36, marginBottom: '8px' }} />
              <p>No tariff history yet.</p>
              <p style={{ fontSize: '13px' }}>
                Re-screen this product to start tracking how its tariffs change over time.
              </p>
            </div>
          ) : (
            history.map(renderEntry)
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TariffHistoryModal;
